import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { billAPI, inventoryAPI, customerAPI, API_BASE_URL } from '../services/api';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import { Loader2, ShieldCheck, Calendar, Download, Edit, Trash2, Search, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import SalesTab from './bills/SalesTab';
import CreateBillModal from './bills/CreateBillModal';
import EditBillModal from './bills/EditBillModal';
import DownloadBillDialog from './bills/DownloadBillDialog';
import ReturnOrderModal from './ReturnOrderModal';
import WarrantyCheckModal from './WarrantyCheckModal';
import PaymentHistoryModal from './PaymentHistoryModal';
import usePersistedForm from '../hooks/usePersistedForm';
import { handleServerErrors } from '../utils/handleServerErrors';
import { notificationService } from '../services/notificationService';

const INITIAL_BILL_STATE = {
    billType: 'Invoice',
    customerName: '',
    customerContactNumber: '',
    customerEmail: '',
    customerGSTNumber: '',
    billingAddressLine: '',
    billingCity: '',
    billingState: 'Maharashtra',
    billingPincode: '',
    shippingAddressLine: '',
    shippingCity: '',
    shippingState: 'Maharashtra',
    shippingPincode: '',
    salesPersonId: (() => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return user.userId || user.id || '01';
    })(),
    purchaseOrderNumber: '',
    orderDate: new Date().toISOString().split('T')[0],
    orderExpiryDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
    orderTime: new Date().toTimeString().split(' ')[0],
    shipmentDate: '',
    deliveryDate: '',
    discountPercent: 0,
    shippingFee: 0,
    businessSaleType: 'B2C',
    paymentMethod: 'CASH',
    advanceAmount: 0,
    customerPaidAmount: 0,
    customerPendingBill: 0,
    paymentStatus: 'PENDING',
    physicalBillAvailableFlg: false,
    physicalBillNumber: '',
    physicalBillFileName: '',
    physicalBillFile: null,
    transactionReferenceNumber: '',
    cancellationReason: '',
    isDealerWarranty: false,
    isAdvanceMerged: false,
    customerNotes: '',
    items: [],
    serviceBills: []
};

const BillPage = ({ onBack }) => {
    const websocketBaseUrl = API_BASE_URL.replace(/\/api\/v\d+\/?$/, '');
    const [bills, setBills] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [backendTotals, setBackendTotals] = useState(null);
    const [totalBillsCount, setTotalBillsCount] = useState(0);
    const [totalPagesFromAPI, setTotalPagesFromAPI] = useState(0);

    const [showCreateBill, setShowCreateBill] = useState(false);
    const [showEditBill, setShowEditBill] = useState(false);
    const [selectedBill, setSelectedBill] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [modalMessage, setModalMessage] = useState(null);
    const [modalErrors, setModalErrors] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [sortBy, setSortBy] = useState('orderDate');
    const [sortDir, setSortDir] = useState('DESC');
    const [showDownloadDialog, setShowDownloadDialog] = useState(false);
    const [billToDownload, setBillToDownload] = useState(null);

    const [showReturnModal, setShowReturnModal] = useState(false);
    const [returnOrderId, setReturnOrderId] = useState(null);
    const [showWarrantyModal, setShowWarrantyModal] = useState(false);
    const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
    const [paymentHistoryOrderId, setPaymentHistoryOrderId] = useState(null);

    const [newBill, setNewBill, resetDraft, reloadDraft] = usePersistedForm(
        'billFormDraft', 
        INITIAL_BILL_STATE, 
        showCreateBill && !showEditBill && !isCreating
    );

    const [billTotals, setBillTotals] = useState({
        subtotal: 0,
        totalDiscount: 0,
        taxableValue: 0,
        totalTax: 0,
        grandTotal: 0,
        totalQuantity: 0,
        shippingFee: 0
    });

    const [isBillModified, setIsBillModified] = useState(false);

    const loadBills = useCallback(async (page, size, status) => {
        try {
            setLoading(true);
            let response;
            if (status && status !== 'all') {
                const paymentStatus = status.toUpperCase();
                response = await billAPI.getBillsByPaymentStatus(paymentStatus, {
                    page: page - 1,
                    size: size
                });
            } else {
                const params = {
                    page: page - 1,
                    size: size
                };
                response = await billAPI.getBills(params);
            }

            const billsData = (response.data.content || []).filter(Boolean);
            setBills(billsData);
            if (response.data.totalElements !== undefined) setTotalBillsCount(response.data.totalElements);
            if (response.data.totalPages !== undefined) setTotalPagesFromAPI(response.data.totalPages);
            setError(null);
        } catch (err) {
            console.error('Error loading bills:', err);
            setError('Failed to load bills.');
            setBills([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Zero-Refresh Real-time Registry Synchronizer
    useEffect(() => {
        const socketUrl = `${websocketBaseUrl}/ws`;
        const stompClient = new Client({
            webSocketFactory: () => new SockJS(socketUrl),
            debug: (str) => { /* console.log('Bill Sync: ' + str); */ },
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
        });

        stompClient.onConnect = (frame) => {
            console.log('Bill Registry connected to live-sync: ' + frame);
            stompClient.subscribe('/topic/dashboard', (message) => {
                if (message.body === 'SYNC') {
                    loadBills(currentPage, pageSize, statusFilter);
                }
            });
        };

        stompClient.activate();

        return () => {
            if (stompClient) stompClient.deactivate();
        };
    }, [loadBills, currentPage, pageSize, statusFilter, websocketBaseUrl]);

    const loadProducts = useCallback(async () => {
        try {
            const response = await inventoryAPI.getProducts({ limit: 1000 });
            setProducts(response.data.content || response.data.data || []);
        } catch (err) {
            console.error('Error loading products:', err);
        }
    }, []);

    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    useEffect(() => {
        loadBills(currentPage, pageSize, statusFilter);
    }, [currentPage, pageSize, statusFilter, loadBills]);

    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    }, [pageSize, statusFilter, currentPage]);

    const addItem = useCallback(() => {
        setNewBill(prev => ({
            ...prev,
            items: [...prev.items, { productId: null, quantitySold: 0, sellingPrice: 0 }]
        }));
        setIsBillModified(true);
    }, [setNewBill]);

    const addServiceBill = useCallback(() => {
        setNewBill(prev => ({
            ...prev,
            serviceBills: [...(prev.serviceBills || []), { description: '', sacCode: '', serviceCost: 0, quantity: 0, gstPercent: 0 }]
        }));
        setIsBillModified(true);
    }, [setNewBill]);

    const updateServiceBill = useCallback((index, field, value) => {
        setNewBill(prev => {
            const updated = [...(prev.serviceBills || [])];
            updated[index] = { ...updated[index], [field]: value };
            return { ...prev, serviceBills: updated };
        });
        setIsBillModified(true);
    }, [setNewBill]);

    const removeServiceBill = useCallback((index) => {
        setNewBill(prev => ({
            ...prev,
            serviceBills: (prev.serviceBills || []).filter((_, i) => i !== index)
        }));
        setIsBillModified(true);
    }, [setNewBill, setIsBillModified]);

    const updateItem = useCallback((index, field, value) => {
        setNewBill(prev => {
            const updatedItems = [...prev.items];
            updatedItems[index] = { ...updatedItems[index], [field]: value };
            return { ...prev, items: updatedItems };
        });
        setIsBillModified(true);
    }, [setNewBill, setIsBillModified]);

    const removeItem = useCallback((index) => {
        setNewBill(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
        setIsBillModified(true);
    }, [setNewBill, setIsBillModified]);

    const calculateTotals = useCallback((items, serviceBills, billType, globalDiscountPercent, shippingFee) => {
        const validItems = Array.isArray(items) ? items.filter(item => (Number(item.quantitySold) || 0) > 0) : [];
        const productsSubtotal = validItems.reduce((sum, item) => sum + ((Number(item.quantitySold) || 0) * (Number(item.sellingPrice) || 0)), 0);
        const validServices = Array.isArray(serviceBills) ? serviceBills : [];
        const servicesSubtotal = validServices.reduce((sum, sb) => sum + ((Number(sb.serviceCost) || 0) * (Number(sb.quantity) || 1)), 0);
        const subtotal = productsSubtotal + servicesSubtotal;
        const totalDiscount = (subtotal * (Number(globalDiscountPercent) || 0)) / 100;
        const productTax = billType === 'Invoice' ? validItems.reduce((sum, item) => sum + ((Number(item.quantitySold) || 0) * (Number(item.sellingPrice) || 0) * ((Number(item.gstPercent) || 0) / 100)), 0) : 0;
        const serviceTax = billType === 'Invoice' ? validServices.reduce((sum, sb) => sum + ((Number(sb.serviceCost) || 0) * (Number(sb.quantity) || 1) * ((Number(sb.gstPercent) || 0) / 100)), 0) : 0;
        const totalTax = productTax + serviceTax;
        const shipFee = Number(shippingFee) || 0;
        const taxableValue = subtotal - totalDiscount;
        const grandTotal = subtotal - totalDiscount + totalTax + shipFee;
        const totalQuantity = validItems.reduce((sum, item) => sum + (Number(item.quantitySold) || 0), 0);
        return { subtotal, totalDiscount, taxableValue, totalTax, grandTotal, totalQuantity };
    }, []);

    useEffect(() => {
        if (showEditBill && !isBillModified) return;
        const computed = calculateTotals(newBill.items, newBill.serviceBills, newBill.billType, newBill.discountPercent, newBill.shippingFee);
        setBillTotals({ ...computed, shippingFee: Number(newBill.shippingFee) || 0 });
    }, [newBill.items, newBill.serviceBills, newBill.billType, newBill.discountPercent, newBill.shippingFee, calculateTotals, showEditBill, isBillModified]);

    const handleBillChange = useCallback((field, value) => {
        setNewBill(prev => ({ ...prev, [field]: value }));
        const totalsFields = ['discountPercent', 'shippingFee', 'billType', 'items', 'serviceBills'];
        if (totalsFields.includes(field)) setIsBillModified(true);
    }, [setNewBill, setIsBillModified]);

    const handleBillTypeChange = useCallback((newBillType) => {
        setNewBill(prev => {
            const updatedItems = prev.items.map(item => {
                const subtotal = (Number(item.quantitySold) || 0) * (Number(item.sellingPrice) || 0);
                const discountAmount = (subtotal * (Number(item.discountPercent) || 0)) / 100;
                const taxableValue = subtotal - discountAmount;
                const taxAmount = (taxableValue * (Number(item.taxPercent) || 0)) / 100;
                return { ...item, totalAmount: taxableValue + taxAmount };
            });
            return { ...prev, billType: newBillType, items: updatedItems };
        });
        setIsBillModified(true);
    }, [setNewBill, setIsBillModified]);

    const handleCustomerSearch = useCallback(async (phone, setMessage, setMessageType) => {
        if (!phone || phone.length !== 10) return;
        try {
            const response = await customerAPI.searchCustomer(phone);
            const data = response.data;
            setNewBill(prev => ({
                ...prev,
                customerName: data.customerName || '',
                customerEmail: data.customerEmail || '',
                customerGSTNumber: data.customerGSTNumber || '',
                billingAddressLine: data.billingAddressLine || '',
                billingCity: data.billingCity || '',
                billingState: data.billingState || 'Maharashtra',
                billingPincode: data.billingPincode || '',
                shippingAddressLine: data.shippingAddressLine || '',
                shippingCity: data.shippingCity || '',
                shippingState: data.shippingState || 'Maharashtra',
                shippingPincode: data.shippingPincode || ''
            }));
            if (setMessage && setMessageType) {
                setMessage('✅ Customer details auto-filled successfully!');
                setMessageType('success');
            }
        } catch (error) {
            console.error('Customer search failed:', error);
        }
    }, [setNewBill]);

    const mapBusinessErrorToField = (message = '') => {
        const normalized = String(message).toLowerCase();
        if (normalized.includes('transaction reference') && normalized.includes('unique')) {
            return 'transactionReferenceNumber';
        }
        return null;
    };

    const handleCreateBill = async () => {
        try {
            setIsCreating(true);
            const currentTotals = calculateTotals(newBill.items, newBill.serviceBills, newBill.billType, newBill.discountPercent, newBill.shippingFee);
            const billData = {
                ...newBill,
                discountPercent: parseFloat(newBill.discountPercent || 0),
                shippingFee: parseFloat(newBill.shippingFee || 0),
                advanceAmount: parseFloat(newBill.advanceAmount || 0),
                customerPaidAmount: parseFloat(newBill.customerPaidAmount || 0),
                customerPendingBill: (() => {
                    const paid = parseFloat(newBill.customerPaidAmount || 0);
                    return currentTotals.grandTotal - paid;
                })(),
                items: newBill.items.map(item => ({
                    productId: parseInt(item.productId),
                    quantitySold: parseInt(item.quantitySold),
                    sellingPrice: parseFloat(item.sellingPrice),
                    gstPercent: item.gstPercent || 0,
                    skuProductCodes: item.skuProductCodes || []
                })),
                includeGst: newBill.billType === 'Invoice'
            };
            await billAPI.createBill(billData, newBill.physicalBillFile);
            setModalMessage({ type: 'success', text: 'Bill created successfully!' });
            setTimeout(() => {
                setShowCreateBill(false);
                setModalMessage(null);
                setCurrentPage(1);
                loadBills(1, pageSize, statusFilter);
                resetDraft();
            }, 3000);
        } catch (err) {
            console.error('Error creating bill:', err);
            const fieldErrors = handleServerErrors(err);
            const hasFieldErrors = Object.keys(fieldErrors).some((key) => key !== '_form');
            if (hasFieldErrors) {
                setModalErrors(fieldErrors);
                setModalMessage({ type: 'error', text: 'Please fix validation errors below.' });
            } else {
                const backendMessage = fieldErrors._form || err.response?.data?.message || 'Failed to create bill. Please try again.';
                const mappedField = mapBusinessErrorToField(backendMessage);
                if (mappedField) {
                    setModalErrors({ [mappedField]: backendMessage });
                    setModalMessage({ type: 'error', text: backendMessage });
                } else {
                    setModalMessage({ type: 'error', text: backendMessage });
                }
            }
        } finally {
            setIsCreating(false);
        }
    };

    const handleUpdateBill = async () => {
        try {
            setIsUpdating(true);
            const currentTotals = calculateTotals(newBill.items, newBill.serviceBills, newBill.billType, newBill.discountPercent, newBill.shippingFee);
            const billData = {
                ...newBill,
                discountPercent: parseFloat(newBill.discountPercent || 0),
                shippingFee: parseFloat(newBill.shippingFee || 0),
                customerPaidAmount: parseFloat(newBill.customerPaidAmount || 0),
                customerPendingBill: (() => {
                    const paid = parseFloat(newBill.customerPaidAmount || 0);
                    return currentTotals.grandTotal - paid;
                })(),
                items: newBill.items.map(item => ({
                    productId: parseInt(item.productId),
                    quantitySold: parseInt(item.quantitySold),
                    sellingPrice: parseFloat(item.sellingPrice),
                    gstPercent: item.gstPercent || 0,
                    skuProductCodes: item.skuProductCodes || []
                })),
                includeGst: newBill.billType === 'Invoice'
            };
            const id = selectedBill.orderId || selectedBill._id;
            await billAPI.updateBill(id, billData);
            setModalMessage({ type: 'success', text: 'Bill updated successfully!' });
            setTimeout(() => {
                setShowEditBill(false);
                setModalMessage(null);
                loadBills(currentPage, pageSize, statusFilter);
            }, 3000);
        } catch (err) {
            console.error('Error updating bill:', err);
            const fieldErrors = handleServerErrors(err);
            const hasFieldErrors = Object.keys(fieldErrors).some((key) => key !== '_form');
            if (hasFieldErrors) {
                setModalErrors(fieldErrors);
                setModalMessage({ type: 'error', text: 'Please fix validation errors below.' });
            } else {
                const backendMessage = fieldErrors._form || err.response?.data?.message || 'Failed to update bill. Please try again.';
                const mappedField = mapBusinessErrorToField(backendMessage);
                if (mappedField) {
                    setModalErrors({ [mappedField]: backendMessage });
                    setModalMessage({ type: 'error', text: backendMessage });
                } else {
                    setModalMessage({ type: 'error', text: backendMessage });
                }
            }
        } finally {
            setIsUpdating(false);
        }
    };

    const handleEditBill = useCallback(async (bill) => {
        try {
            const id = bill.orderId || bill.id || bill._id;
            const [billRes, financialRes] = await Promise.all([billAPI.getBill(id), billAPI.getFinancialDetails(id)]);
            const fullBill = billRes.data;
            const financial = financialRes.data;
            setIsBillModified(false);
            setBackendTotals(financial);
            setSelectedBill(fullBill);
            setNewBill({
                ...fullBill,
                billType: fullBill.includeGst !== false ? 'Invoice' : 'Bill',
                items: fullBill.items?.map(item => ({
                    ...item,
                    productId: item.productId,
                    quantitySold: item.quantitySold || 1,
                    sellingPrice: item.sellingPrice || 0
                })) || []
            });
            setShowEditBill(true);
        } catch (err) {
            console.error('Error loading bill for edit:', err);
        }
    }, [setNewBill]);

    const handleCloseEditBill = useCallback((open) => {
        if (!open) {
            setShowEditBill(false);
            setSelectedBill(null);
            setNewBill(INITIAL_BILL_STATE);
        }
    }, [setNewBill]);

    const handleCloseCreateBill = useCallback((open) => {
        if (!open) setShowCreateBill(false);
    }, []);

    const generatePDF = useCallback((bill) => {
        setBillToDownload(bill);
        setShowDownloadDialog(true);
    }, []);

    const handleDownloadWithGst = useCallback(async (includeGst) => {
        if (!billToDownload) return;
        try {
            const id = billToDownload.orderId || billToDownload.id;
            const response = await billAPI.generatePDF(id, includeGst);
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = `invoice-${id}.pdf`;
            link.click();
        } catch (err) {
            console.error('PDF generation failed:', err);
        }
    }, [billToDownload]);

    const handleDownloadPhysicalBill = useCallback(async (bill) => {
        try {
            const id = bill.orderId || bill.id;
            const response = await billAPI.downloadPhysicalBill(id);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = bill.physicalBillFileName || `physical-bill-${id}`;
            link.click();
        } catch (err) {
            console.error('Physical bill download failed:', err);
        }
    }, []);

    const handleDeleteBill = useCallback(async (id) => {
        if (window.confirm('Are you sure you want to delete this bill?')) {
            try {
                await billAPI.deleteBill(id);
                setError(null);
                notificationService.showSuccess('Bill deleted successfully!');
                loadBills(currentPage, pageSize, statusFilter);
            } catch (err) {
                console.error('Delete failed:', err);
                const msg = err.response?.data?.message || err.message || 'Failed to delete bill.';
                setError(msg);
                notificationService.showError(msg);
            }
        }
    }, [loadBills, currentPage, pageSize, statusFilter]);

    const handleHoldOrder = useCallback(async (id) => {
        try {
            await billAPI.holdOrder(id);
            loadBills(currentPage, pageSize, statusFilter);
        } catch (err) {
            console.error('Hold failed:', err);
        }
    }, [loadBills, currentPage, pageSize, statusFilter]);

    const handleReturnOrder = useCallback((id) => {
        setReturnOrderId(id);
        setShowReturnModal(true);
    }, []);

    const handleReturnSuccess = useCallback(() => {
        loadBills(currentPage, pageSize, statusFilter);
    }, [loadBills, currentPage, pageSize, statusFilter]);

    const handleViewPaymentHistory = useCallback((id) => {
        setPaymentHistoryOrderId(id);
        setShowPaymentHistoryModal(true);
    }, []);

    const handleOpenCreateBill = useCallback(() => {
        reloadDraft();
        setShowCreateBill(true);
    }, [reloadDraft]);

    const handleClearDraft = useCallback(() => {
        resetDraft();
    }, [resetDraft]);

    const filteredAndSortedBills = useMemo(() => {
        let filtered = [...bills];
        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            filtered = filtered.filter(b => b.customerName?.toLowerCase().includes(lower) || b.invoiceNumber?.toLowerCase().includes(lower));
        }
        filtered.sort((a, b) => {
            const aVal = a[sortBy];
            const bVal = b[sortBy];
            if (aVal < bVal) return sortDir === 'ASC' ? -1 : 1;
            if (aVal > bVal) return sortDir === 'ASC' ? 1 : -1;
            return 0;
        });
        return filtered;
    }, [bills, searchTerm, sortBy, sortDir]);

    const totalPages = totalPagesFromAPI > 0 ? totalPagesFromAPI : 1;
    const totalBills = totalBillsCount > 0 ? totalBillsCount : filteredAndSortedBills.length;

    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, pageSize, dateFilter, sortBy, sortDir]);

    const logo = require('../lib/logo.png');

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white/85 dark:bg-slate-900/75 shadow-sm">
                <div className="flex items-center gap-3">
                    <img src={logo} alt="JIT Logo" className="h-8 w-8 object-contain" />
                    <div>
                        <div className="text-sm font-bold text-slate-800 dark:text-slate-100">JIT Billing</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">Invoice & Service Management</div>
                    </div>
                </div>
            </div>
            <SalesTab
                onBack={onBack}
                error={error}
                setError={setError}
                loadBills={loadBills}
                setShowCreateBill={handleOpenCreateBill}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
                filteredBills={filteredAndSortedBills}
                totalBills={totalBills}
                sortBy={sortBy}
                sortDir={sortDir}
                onSortChange={(field, dir) => { setSortBy(field); setSortDir(dir); }}
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
                handleEditBill={handleEditBill}
                handleDeleteBill={handleDeleteBill}
                handleHoldOrder={handleHoldOrder}
                handleReturnOrder={handleReturnOrder}
                setShowWarrantyModal={setShowWarrantyModal}
                handleViewPaymentHistory={handleViewPaymentHistory}
                generatePDF={generatePDF}
                downloadPhysicalBill={handleDownloadPhysicalBill}
            />

            {showCreateBill && (
                <CreateBillModal
                    open={showCreateBill}
                    onOpenChange={handleCloseCreateBill}
                    newBill={newBill}
                    onBillChange={handleBillChange}
                    onBillTypeChange={handleBillTypeChange}
                    onAddItem={addItem}
                    onUpdateItem={updateItem}
                    onRemoveItem={removeItem}
                    onAddServiceBill={addServiceBill}
                    onUpdateServiceBill={updateServiceBill}
                    onRemoveServiceBill={removeServiceBill}
                    totals={billTotals}
                    products={products}
                    onCreate={handleCreateBill}
                    isLoading={isCreating}
                    modalMessage={modalMessage}
                    modalErrors={modalErrors}
                    onClearModalErrors={() => setModalErrors({})}
                    onCustomerSearch={handleCustomerSearch}
                    loadProducts={loadProducts}
                    onClearDraft={handleClearDraft}
                />
            )}

            {showEditBill && (
                <EditBillModal
                    open={showEditBill}
                    onOpenChange={handleCloseEditBill}
                    selectedBill={selectedBill}
                    newBill={newBill}
                    onBillChange={handleBillChange}
                    onBillTypeChange={handleBillTypeChange}
                    onUpdateItem={updateItem}
                    onAddItem={addItem}
                    onRemoveItem={removeItem}
                    onAddServiceBill={addServiceBill}
                    onUpdateServiceBill={updateServiceBill}
                    onRemoveServiceBill={removeServiceBill}
                    totals={billTotals}
                    backendTotals={backendTotals}
                    products={products}
                    onUpdate={handleUpdateBill}
                    isLoading={isUpdating}
                    modalMessage={modalMessage}
                    modalErrors={modalErrors}
                    onClearModalErrors={() => setModalErrors({})}
                    onCustomerSearch={handleCustomerSearch}
                    loadProducts={loadProducts}
                />
            )}

            <DownloadBillDialog
                open={showDownloadDialog}
                onClose={() => { setShowDownloadDialog(false); setBillToDownload(null); }}
                onDownload={handleDownloadWithGst}
                billNumber={billToDownload?.invoiceNumber || billToDownload?.orderId}
            />

            {showReturnModal && (
                <ReturnOrderModal
                    orderId={returnOrderId}
                    onClose={() => { setShowReturnModal(false); setReturnOrderId(null); }}
                    onSuccess={handleReturnSuccess}
                />
            )}

            {showWarrantyModal && <WarrantyCheckModal onClose={() => setShowWarrantyModal(false)} />}
            {showPaymentHistoryModal && <PaymentHistoryModal orderId={paymentHistoryOrderId} onClose={() => { setShowPaymentHistoryModal(false); setPaymentHistoryOrderId(null); }} />}
        </div>
    );
};

export default BillPage;
